import { useState, useRef, useEffect } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { languageMap } from "@/i18n";
import { BookingLanguage } from "@/types/booking";
import { type MarketCountry } from "@/config/market";
import { useMarket } from "@/hooks/useMarket";
import { useAvailableMarkets } from "@/hooks/useAvailableMarkets";
import { trackCountrySwitched } from "@/utils/analytics";

interface CountrySelectorProps {
  language: BookingLanguage;
  skeletonMode?: boolean;
}

/**
 * Header market (country) chooser. Mirrors the LanguageSelector visually.
 *
 * Switching country restarts the booking flow from the first step (handled by
 * the store's `setMarket`), because clinics, prices and availability all differ
 * per market. Only renders when more than one market is bookable.
 */
const CountrySelector = ({
  language,
  skeletonMode = false,
}: CountrySelectorProps) => {
  const { t } = useTranslation(undefined, { lng: languageMap[language] });
  const { market, setMarket } = useMarket();
  const availableMarkets = useAvailableMarkets();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [open]);

  // Nothing to choose between — keep the header clean.
  if (availableMarkets.length < 2) return null;

  const labelFor = (m: MarketCountry) => t(`countrySelector.${m}`);

  const handleSelect = (value: MarketCountry) => {
    setOpen(false);
    if (value !== market) {
      trackCountrySwitched(market, value);
      setMarket(value);
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t("countrySelector.label")}
        data-testid="country-selector"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "inline-flex items-center gap-1 rounded-full border px-3 py-1",
          "text-sm ring-offset-background",
          "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
          "disabled:cursor-not-allowed disabled:opacity-50",
          "[&>span]:line-clamp-1",
          "h-8 md:h-10 bg-transparent border-transparent shadow-none hover:border-gray-200",
          "font-sans normal-case",
        )}
      >
        <span style={{ pointerEvents: "none" }}>
          {skeletonMode ? " " : labelFor(market)}
        </span>
        <ChevronDown className="h-4 w-4 opacity-50" aria-hidden="true" />
      </button>

      {open && (
        <div
          role="listbox"
          className={cn(
            "absolute right-0 top-full mt-1 z-50 min-w-[10rem]",
            "overflow-hidden rounded-md border bg-popover p-1",
            "text-popover-foreground shadow-md",
            "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2",
            "font-sans",
          )}
        >
          {availableMarkets.map((value) => (
            <button
              key={value}
              type="button"
              role="option"
              aria-selected={market === value}
              data-testid={`country-option-${value}`}
              onClick={() => handleSelect(value)}
              className={cn(
                "relative flex w-full cursor-default select-none items-center",
                "rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none",
                "hover:bg-accent hover:text-accent-foreground",
                "normal-case font-sans",
              )}
            >
              <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                {market === value && <Check className="h-4 w-4" />}
              </span>
              {labelFor(value)}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CountrySelector;
